import { useState, useEffect } from "react";
import { Shield, X } from "lucide-react";

const CONSENT_KEY = "oc_cookie_consent";
const CONSENT_TIMESTAMP_KEY = "oc_cookie_consent_ts";

export const getConsent = () => localStorage.getItem(CONSENT_KEY);
export const hasConsented = () => getConsent() === "accepted";

export default function CookieConsentBanner({ onAccept }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const consent = getConsent();
    if (!consent) {
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
    if (consent === "accepted" && onAccept) onAccept();
  }, [onAccept]);

  const handleAccept = () => {
    localStorage.setItem(CONSENT_KEY, "accepted");
    localStorage.setItem(CONSENT_TIMESTAMP_KEY, new Date().toISOString());
    setVisible(false);
    if (onAccept) onAccept();
  };

  const handleDecline = () => {
    localStorage.setItem(CONSENT_KEY, "declined");
    localStorage.setItem(CONSENT_TIMESTAMP_KEY, new Date().toISOString());
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-consent-banner"
      className="fixed bottom-0 left-0 right-0 z-[9999] animate-in slide-in-from-bottom duration-500"
    >
      <div className="bg-slate-900 border-t border-slate-700 shadow-2xl">
        <div className="max-w-6xl mx-auto px-4 py-4 sm:px-6 sm:py-5">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <Shield className="w-6 h-6 text-orange-400 flex-shrink-0 hidden sm:block" />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-slate-200 leading-relaxed">
                We use cookies and tracking technologies to improve your experience, analyze site traffic, and support our marketing efforts.
                By clicking "Accept All," you consent to our use of cookies.{" "}
                <a
                  href="/privacy"
                  className="text-orange-400 hover:text-orange-300 underline underline-offset-2"
                >
                  Privacy Policy
                </a>
              </p>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0 w-full sm:w-auto">
              <button
                data-testid="cookie-decline-btn"
                onClick={handleDecline}
                className="flex-1 sm:flex-initial px-4 py-2 text-sm text-slate-400 hover:text-white border border-slate-600 hover:border-slate-500 rounded-lg transition-colors"
              >
                Decline
              </button>
              <button
                data-testid="cookie-accept-btn"
                onClick={handleAccept}
                className="flex-1 sm:flex-initial px-5 py-2 text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors"
              >
                Accept All
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
